import { useMemo } from 'react';
import { useTypedSelector } from '../app/store';
import {
    useGetEnvironmentalStressMonitoringMetricsQuery,
    useGetEnvironmentalStressMonitoringFilterQuery,
    useSearchEnvironmentalStressMonitoringQuery,
} from '../features/prediction/predictionApi';
import {
    toEnvironmentalStressMonitoringData,
    type EnvironmentalFilterResponse,
    type EnvironmentalMetricsResponse,
    type EnvironmentalSearchResponse,
    type EnvironmentalStressMonitoringFilterRequest,
    type EnvironmentalStressMonitoringRequest,
} from '../features/prediction/environmentalStressMonitoringApi';
import type {
    EnvironmentalStressMonitoringEmptySections,
    EnvironmentalStressMonitoringData,
    EnvironmentalStressMonitoringWindow,
} from '../constants/environmentalStressMonitoring.model';

type EnvironmentalStressMonitoringDataResult = {
    dailyData?: EnvironmentalStressMonitoringData;
    monthlyData?: EnvironmentalStressMonitoringData;
    emptyResponses: EnvironmentalStressMonitoringEmptySections;
    isLoading: boolean;
    isError: boolean;
};

type QueryState<T = unknown> = {
    currentData?: T;
    isError: boolean;
    isFetching: boolean;
    isLoading: boolean;
};

const getQueryStatus = (
    states: QueryState[],
    hasData: boolean,
    loadingStates: QueryState[] = states
): Pick<EnvironmentalStressMonitoringDataResult, 'isLoading' | 'isError'> => ({
    isLoading: loadingStates.some((state) => state.isLoading || (state.isFetching && !state.currentData)),
    isError: !hasData && states.some((state) => state.isError),
});

const hasResponse = (...responses: unknown[]): boolean => responses.some(Boolean);

const getConfiguredDeviceId = (
    filters: { deviceIds?: unknown; deviceId?: unknown },
    storedDeviceId: string | undefined
): string | undefined => {
    if (Array.isArray(filters.deviceIds) && typeof filters.deviceIds[0] === 'string') return filters.deviceIds[0];
    if (typeof filters.deviceId === 'string') return filters.deviceId;
    return storedDeviceId;
};

const shouldSkipDataQuery = (
    deviceId?: string,
    organizationId?: string,
    parameter?: string,
    hasMetrics?: boolean
): boolean => !deviceId || !organizationId || !parameter || !hasMetrics;

const getSelectedParameter = (
    selectedParameter: string | undefined,
    metrics: EnvironmentalMetricsResponse | undefined
): string | undefined => {
    const parameters = metrics?.data?.parameters ?? [];
    const selected = parameters.find((item) => item.parameter === selectedParameter)?.parameter;
    return selected ?? parameters[0]?.parameter;
};

const mapActiveData = (
    metrics?: EnvironmentalMetricsResponse,
    filter?: EnvironmentalFilterResponse,
    search?: EnvironmentalSearchResponse
): EnvironmentalStressMonitoringData | undefined =>
    hasResponse(metrics, filter, search) ? toEnvironmentalStressMonitoringData(metrics, filter, search) : undefined;

const getDataForWindow = (
    window: EnvironmentalStressMonitoringWindow,
    data: EnvironmentalStressMonitoringData | undefined
): Pick<EnvironmentalStressMonitoringDataResult, 'dailyData' | 'monthlyData'> => ({
    dailyData: window === 'DAILY' ? data : undefined,
    monthlyData: window === 'MONTHLY' ? data : undefined,
});

const getRequest = (
    deviceId: string | undefined,
    window: EnvironmentalStressMonitoringRequest['window']
): EnvironmentalStressMonitoringRequest | undefined => (deviceId ? { deviceId, window } : undefined);

const isQueryEmpty = (query: QueryState, count: number = 0): boolean =>
    !query.isLoading && !query.isFetching && (query.isError || count === 0);

const getEmptyResponses = (
    metrics: QueryState<EnvironmentalMetricsResponse>,
    filter: QueryState<EnvironmentalFilterResponse>,
    search: QueryState<EnvironmentalSearchResponse>
): EnvironmentalStressMonitoringEmptySections => ({
    metrics: isQueryEmpty(metrics, metrics.currentData?.data?.parameters?.length),
    notifications: isQueryEmpty(filter, filter.currentData?.data?.records?.length),
    chart: isQueryEmpty(search, search.currentData?.data?.length),
});

export const useEnvironmentalStressMonitoringData = (
    deviceId?: string,
    activeWindow: EnvironmentalStressMonitoringWindow = 'DAILY',
    selectedParameter?: string,
    requestedOrganizationId?: string,
    waitForDependentQueries = false,
    timeWindow?: number,
    enabled = true
): EnvironmentalStressMonitoringDataResult => {
    const selectedOrganizationId = useTypedSelector((state) => state.org.selectedOrg?.id);
    const configuredDeviceId = useTypedSelector((state) => {
        const { deviceId: storedDeviceId, filters } = state.environmental_stress_monitoring;
        return getConfiguredDeviceId(filters, storedDeviceId);
    });
    const effectiveDeviceId = deviceId ?? configuredDeviceId;
    const organizationId = requestedOrganizationId ?? selectedOrganizationId;
    const requestedTimeWindow = timeWindow;
    const baseRequest = {
        ...getRequest(effectiveDeviceId, activeWindow),
        organizationId,
    } as EnvironmentalStressMonitoringRequest;
    const metricsRequest = {
        ...baseRequest,
        ...(requestedTimeWindow !== undefined ? { timeWindow: requestedTimeWindow } : {}),
    } as EnvironmentalStressMonitoringRequest;
    const metrics = useGetEnvironmentalStressMonitoringMetricsQuery(metricsRequest, {
        skip: !enabled || !effectiveDeviceId,
        refetchOnMountOrArgChange: true,
    });
    const currentMetrics = metrics.currentData;
    const parameter = getSelectedParameter(selectedParameter, currentMetrics);
    const filterRequest = {
        ...baseRequest,
        ...(parameter !== undefined ? { parameters: [parameter] } : {}),
        ...(requestedTimeWindow !== undefined ? { timeWindow: requestedTimeWindow } : {}),
    } as EnvironmentalStressMonitoringFilterRequest;
    const filter = useGetEnvironmentalStressMonitoringFilterQuery(filterRequest, {
        skip: !enabled || shouldSkipDataQuery(effectiveDeviceId, organizationId, parameter, Boolean(currentMetrics)),
        refetchOnMountOrArgChange: true,
    });
    const searchRequest = {
        ...baseRequest,
        parameter,
        ...(requestedTimeWindow !== undefined ? { timeWindow: requestedTimeWindow } : {}),
    } as EnvironmentalStressMonitoringRequest;
    const search = useSearchEnvironmentalStressMonitoringQuery(searchRequest, {
        skip: !enabled || shouldSkipDataQuery(effectiveDeviceId, organizationId, parameter, Boolean(currentMetrics)),
        refetchOnMountOrArgChange: true,
    });
    const activeData = useMemo(
        () => mapActiveData(currentMetrics, filter.currentData, search.currentData),
        [currentMetrics, filter.currentData, search.currentData]
    );
    const hasData = hasResponse(metrics.currentData, filter.currentData, search.currentData);
    const status = getQueryStatus(
        [metrics, filter, search],
        hasData,
        waitForDependentQueries ? [metrics, filter, search] : [metrics]
    );
    return {
        ...getDataForWindow(activeWindow, activeData),
        emptyResponses: getEmptyResponses(metrics, filter, search),
        ...status,
    };
};
